// frontend/src/components/asset/ChangeOffenceDriverModal.js
/**
 * Name a different driver on a traffic offence notice.
 *
 * Used when custody records found nobody, or when the proposed driver disputed
 * it. The previous round is superseded rather than overwritten, so the chain of
 * who was proposed and why they were ruled out survives as evidence.
 *
 * Theme: Admin (red).
 */

import React, { useState } from 'react';
import { UserCheck, AlertTriangle } from 'lucide-react';
import Modal from '../UI/Modal';
import SearchableCombobox from '../UI/SearchableCombobox';
import { useStaffList } from '../../hooks/useStaffData';
import { useDebounce } from '../../hooks/useDebounce';
import { useProposeOffenceDriver } from '../../hooks/useAssetInventoryData';

const ChangeOffenceDriverModal = ({ isOpen, onClose, notice }) => {
  const [staffId, setStaffId] = useState('');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: staffData, isLoading } = useStaffList({
    search: debouncedSearch.trim().length >= 2 ? debouncedSearch : undefined,
    page_size: 10,
  });
  const staffList = staffData?.results || [];

  const proposeDriver = useProposeOffenceDriver();

  const handleClose = () => {
    setStaffId('');
    setReason('');
    setSearch('');
    onClose();
  };

  const current = (notice?.declarations || []).find((d) => d.is_current);
  const wasDisputed = current?.response === 'disputed';

  const handleSubmit = () => {
    if (!staffId) return;
    proposeDriver.mutate(
      { id: notice.id, driver_staff_id: staffId, override_reason: reason.trim() },
      { onSuccess: handleClose },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={wasDisputed ? 'Propose a Different Driver' : 'Select Driver'}
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={handleClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!staffId || proposeDriver.isPending}
            className="btn btn-admin-primary"
          >
            <UserCheck className="w-4 h-4 mr-2" />
            {proposeDriver.isPending ? 'Sending...' : 'Ask Them to Confirm'}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        Notice <span className="font-medium text-gray-900">{notice?.notice_number}</span> for{' '}
        {notice?.vehicle_number_raw}.
      </p>

      {wasDisputed && current?.dispute_reason && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-900 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {current.driver_name} disputed this
          </p>
          <p className="text-xs text-red-800 mt-1">&ldquo;{current.dispute_reason}&rdquo;</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Driver <span className="text-red-600">*</span>
        </label>
        <SearchableCombobox
          items={staffList}
          value={staffId}
          onChange={setStaffId}
          onSearch={setSearch}
          loading={isLoading}
          displayFn={(s) => `${s.first_name} ${s.last_name}`}
          valueFn={(s) => s.staff_id}
          searchFn={(s, term) =>
            `${s.first_name} ${s.last_name}`.toLowerCase().includes(term)
            || s.staff_id?.toLowerCase().includes(term)
          }
          placeholder="Search by name or staff ID..."
          searchPlaceholder="Search employees..."
          emptyLabel="No employees found"
          variant="form"
          theme="admin"
          className="w-full"
          renderItem={(s) => (
            <>
              <span className="flex-1">{s.first_name} {s.last_name}</span>
              <span className="text-xs text-gray-400 font-mono">{s.staff_id}</span>
            </>
          )}
        />
      </div>

      <div>
        <label htmlFor="offence-override-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Why this driver?
        </label>
        <input
          id="offence-override-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Site roster shows they took the lorry that morning"
          className="input-admin w-full"
        />
        <p className="text-xs text-gray-500 mt-1">
          Recorded against this proposal so the reasoning survives if it&apos;s questioned later.
        </p>
      </div>

      <p className="text-xs text-gray-500">
        They will be notified in My Assets and asked to confirm or dispute before any
        particulars are furnished to the authority.
      </p>
    </Modal>
  );
};

export default ChangeOffenceDriverModal;
